import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle, Wifi } from 'lucide-react';

import { apiService } from '../../services/apiService';

interface PrinterStatus {
  status: 'online' | 'offline' | 'unknown';
  message: string;
}

interface LabelPrinterStatusProps {
  printerId?: string | null;
}

export const LabelPrinterStatus: React.FC<LabelPrinterStatusProps> = ({
  printerId,
}) => {
  const { t } = useTranslation();
  const [printerStatus, setPrinterStatus] = useState<PrinterStatus>({
    status: 'unknown',
    message: t('labelPrinting.checkingStatus', {
      defaultValue: 'Checking printer status...',
    }),
  });

  useEffect(() => {
    let cancelled = false;

    // No selection yet — nothing to check. Avoid an API call that would
    // need to invent a default printer server-side.
    if (!printerId) {
      setPrinterStatus({
        status: 'unknown',
        message: t('labelPrinting.noPrinterSelected', {
          defaultValue: 'Select a printer to check status',
        }),
      });
      return;
    }

    const checkStatus = async () => {
      setPrinterStatus({
        status: 'unknown',
        message: t('labelPrinting.checkingStatus', {
          defaultValue: 'Checking printer status...',
        }),
      });
      try {
        const response = await apiService.checkPrinterHealth(printerId);
        if (cancelled) return;

        if (response.status === 'online') {
          setPrinterStatus({
            status: 'online',
            message: t('labelPrinting.printerOnline', {
              defaultValue: 'Printer is online and ready',
            }),
          });
        } else {
          setPrinterStatus({
            status: 'offline',
            message: t('labelPrinting.printerOffline', {
              defaultValue: 'Printer is offline',
            }),
          });
        }
      } catch {
        if (cancelled) return;
        setPrinterStatus({
          status: 'offline',
          message: t('labelPrinting.printerUnreachable', {
            defaultValue: 'Printer is unreachable',
          }),
        });
      }
    };

    // Re-check whenever the selected printer changes
    checkStatus();
    return () => {
      cancelled = true;
    };
  }, [t, printerId]);

  const getIcon = () => {
    switch (printerStatus.status) {
      case 'online':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'offline':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Wifi className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = () => {
    switch (printerStatus.status) {
      case 'online':
        return 'bg-green-50 border-green-200 text-green-900';
      case 'offline':
        return 'bg-red-50 border-red-200 text-red-900';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-900';
    }
  };

  return (
    <div
      className={`border rounded-lg p-3 flex items-center gap-3 ${getStatusColor()}`}
    >
      {getIcon()}
      <div className="flex-1">
        <h3 className="text-sm font-medium">
          {t('labelPrinting.printerStatus', {
            defaultValue: 'Printer Status',
          })}
        </h3>
        <p className="text-xs opacity-75">{printerStatus.message}</p>
      </div>
    </div>
  );
};
